import type { LocalProfile } from "./auth-types";

export type SignupIntent = "teacher" | "student";

export type ResolvedSignupIntent = SignupIntent | "ambiguous";

export const SIGNUP_INTENT_KEY = "ccs.auth.signup_intent";

/**
 * Temporary kill switch for choosing Teacher during first-login onboarding.
 * Keep in sync with services/api `TEACHER_SELF_SERVICE_SIGNUP_ENABLED`.
 * Set to true to restore the teacher option.
 */
export const TEACHER_SELF_SERVICE_SIGNUP_ENABLED = false;

export function parseSignupIntentParam(raw: string | null | undefined): SignupIntent | null {
  if (raw === "student") return "student";
  if (raw === "teacher" && TEACHER_SELF_SERVICE_SIGNUP_ENABLED) return "teacher";
  return null;
}

export function saveSignupIntent(intent: SignupIntent | null | undefined): void {
  if (!intent) return;
  if (intent === "teacher" && !TEACHER_SELF_SERVICE_SIGNUP_ENABLED) return;
  try {
    sessionStorage.setItem(SIGNUP_INTENT_KEY, intent);
  } catch {
    /* storage unavailable */
  }
}

export function loadSignupIntent(): SignupIntent | null {
  try {
    return parseSignupIntentParam(sessionStorage.getItem(SIGNUP_INTENT_KEY));
  } catch {
    return null;
  }
}

export function clearSignupIntent(): void {
  try {
    sessionStorage.removeItem(SIGNUP_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve first-login funnel from saved returnTo and signup intent.
 * Precedence: returnTo /join → student, then stored intent, else ambiguous.
 * While teacher self-signup is paused, pending users always get the student funnel.
 */
export function resolveSignupIntent(
  profile: LocalProfile | null | undefined,
  returnTo?: string | null
): ResolvedSignupIntent {
  if (!profile || profile.role !== "pending") return "ambiguous";

  if (returnTo?.startsWith("/join")) return "student";

  if (!TEACHER_SELF_SERVICE_SIGNUP_ENABLED) return "student";

  const stored = loadSignupIntent();
  if (stored === "teacher") return "teacher";
  if (stored === "student") return "student";

  return "ambiguous";
}

export function isStudentSignupIntent(
  profile: LocalProfile | null | undefined,
  returnTo?: string | null
): boolean {
  return resolveSignupIntent(profile, returnTo) === "student";
}
